import React, { useCallback, useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import * as XLSX from 'xlsx'
import {
  Stack, Button, TextField, Accordion, AccordionSummary, AccordionDetails,
  Chip, Table, TableHead, TableBody, TableRow, TableCell, Typography, Alert,
} from '@mui/material'
import { ExpandMore } from '@mui/icons-material'

import { formatDateToISOShort } from '../../../common/utilities/FormatDate'
import {
  runVisitsParityCheck,
  runInvitationsParityCheck,
  runInvitationsTodayParityCheck,
  runComesFromParityCheck,
  runBlackListParityCheck,
  runEmployeesParityCheck,
} from '../lib/parityChecks'

const todayISO = formatDateToISOShort(new Date())

// 'invitations_today' is a fixed critical-path check (always audits the
// current day, ignoring the date pickers below) — see
// `runInvitationsTodayParityCheck` for why it demands exact parity while the
// others tolerate expected drift from the local 90-day window / comes_from
// top-N.
const ENTITIES = ['visits', 'invitations', 'invitations_today', 'comes_from', 'black_list', 'employees']

function DiffSetSummary({ result, t, strict = false }) {
  const mismatchedKeys = new Set(result.mismatched.map(m => m.key)).size
  // Non-strict checks (arbitrary date range) treat onlyInLegacy/onlyInBatch as
  // a warning worth investigating; the strict "today" check treats ANY drift
  // as a hard failure, since VisitForm's offline autocomplete depends on it.
  const driftColor = count => (count ? (strict ? 'error' : 'warning') : 'success')
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap">
      <Chip label={`${t('audit_parity_legacy_count')}: ${result.legacyCount}`} />
      <Chip label={`${t('audit_parity_batch_count')}: ${result.batchCount}`} />
      <Chip label={`${t('audit_parity_matched')}: ${result.matched}`} color="default" />
      <Chip label={`${t('audit_parity_mismatched')}: ${mismatchedKeys}`} color={mismatchedKeys ? 'error' : 'success'} />
      <Chip label={`${t('audit_parity_only_legacy')}: ${result.onlyInLegacy.length}`} color={driftColor(result.onlyInLegacy.length)} />
      <Chip label={`${t('audit_parity_only_batch')}: ${result.onlyInBatch.length}`} color={driftColor(result.onlyInBatch.length)} />
      {result.truncated && <Chip label={t('audit_parity_truncated')} color="warning" variant="outlined" />}
    </Stack>
  )
}

function DiffSetDetails({ result, t }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{t('audit_parity_col_key')}</TableCell>
          <TableCell>{t('audit_parity_col_field')}</TableCell>
          <TableCell>{t('audit_parity_col_legacy')}</TableCell>
          <TableCell>{t('audit_parity_col_batch')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {result.mismatched.map((m, i) => (
          <TableRow key={i}>
            <TableCell>{String(m.key)}</TableCell>
            <TableCell>{m.field}</TableCell>
            <TableCell>{String(m.legacyValue)}</TableCell>
            <TableCell>{String(m.batchValue)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function SubsetSummary({ result, t, boundedNote }) {
  if (result.skipped) return <Typography className="audit-section-note">{t('audit_parity_skipped_disabled')}</Typography>
  return (
    <Stack spacing={1}>
      {boundedNote && <Typography className="audit-section-note">{boundedNote}</Typography>}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Chip label={`${t('audit_parity_legacy_count')}: ${result.legacyCount}`} />
        <Chip label={`${t('audit_parity_batch_count')}: ${result.batchCount}`} />
        <Chip
          label={result.isSubset ? t('audit_parity_subset_ok') : t('audit_parity_subset_missing', { count: result.missingFromBatch.length })}
          // Missing entries are expected when the local set is intentionally
          // bounded (comes_from top-N) — downgrade to a warning in that case
          // instead of a hard error.
          color={result.isSubset ? 'success' : (boundedNote ? 'warning' : 'error')}
        />
      </Stack>
    </Stack>
  )
}

function BlackListSummary({ result, t }) {
  return (
    <Stack spacing={1}>
      <Chip
        label={result.isConsistent ? t('audit_parity_consistent') : t('audit_parity_mismatched_count', { count: result.mismatches.length })}
        color={result.isConsistent ? 'success' : 'error'}
        sx={{ alignSelf: 'flex-start' }}
      />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('Identification')}</TableCell>
            <TableCell>{t('audit_parity_col_legacy_hit')}</TableCell>
            <TableCell>{t('audit_parity_col_batch_hit')}</TableCell>
            <TableCell>{t('audit_parity_col_match')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.checked.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.identification}</TableCell>
              <TableCell>{String(r.legacyHit)}</TableCell>
              <TableCell>{String(r.batchHit)}</TableCell>
              <TableCell>{r.match ? '✅' : '❌'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  )
}

function buildExportWorkbook(results, t) {
  const workbook = XLSX.utils.book_new()

  const diffSetSheet = (name, result) => {
    if (!result) return
    const rows = result.mismatched.map(m => ({ key: m.key, field: m.field, legacy: m.legacyValue, batch: m.batchValue }))
    const sheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{ info: t('audit_parity_no_mismatches') }])
    XLSX.utils.book_append_sheet(workbook, sheet, name)
  }

  diffSetSheet('visits', results.visits)
  diffSetSheet('invitations', results.invitations)
  diffSetSheet('invitations_today', results.invitations_today)

  if (results.comes_from) {
    const sheet = XLSX.utils.json_to_sheet(
      results.comes_from.missingFromBatch.length ? results.comes_from.missingFromBatch : [{ info: t('audit_parity_no_mismatches') }]
    )
    XLSX.utils.book_append_sheet(workbook, sheet, 'comes_from_missing')
  }
  if (results.employees) {
    const sheet = XLSX.utils.json_to_sheet(
      results.employees.missingFromBatch?.length ? results.employees.missingFromBatch : [{ info: t('audit_parity_no_mismatches') }]
    )
    XLSX.utils.book_append_sheet(workbook, sheet, 'employees_missing')
  }
  if (results.black_list) {
    const sheet = XLSX.utils.json_to_sheet(results.black_list.checked)
    XLSX.utils.book_append_sheet(workbook, sheet, 'black_list')
  }

  return workbook
}

function ParityCheckPanel({ isOnline, enabledVisitHost }) {
  const { t } = useTranslation()
  const [startDate, setStartDate] = useState(todayISO)
  const [endDate, setEndDate] = useState(todayISO)
  const [results, setResults] = useState({})
  const [errors, setErrors] = useState({})
  const [runningEntity, setRunningEntity] = useState(null)

  const runOne = useCallback(async (entity) => {
    setRunningEntity(entity)
    setErrors(prev => ({ ...prev, [entity]: null }))
    try {
      let result
      if (entity === 'visits') result = await runVisitsParityCheck({ startDate, endDate })
      else if (entity === 'invitations') result = await runInvitationsParityCheck({ startDate, endDate })
      else if (entity === 'invitations_today') result = await runInvitationsTodayParityCheck()
      else if (entity === 'comes_from') result = await runComesFromParityCheck()
      else if (entity === 'black_list') result = await runBlackListParityCheck()
      else if (entity === 'employees') result = await runEmployeesParityCheck({ enabledVisitHost })
      setResults(prev => ({ ...prev, [entity]: result }))
    } catch (err) {
      setErrors(prev => ({ ...prev, [entity]: err.message }))
    } finally {
      setRunningEntity(null)
    }
  }, [startDate, endDate, enabledVisitHost])

  const runAll = useCallback(async () => {
    for (const entity of ENTITIES) {
      // eslint-disable-next-line no-await-in-loop
      await runOne(entity)
    }
  }, [runOne])

  const handleExport = useCallback(() => {
    const workbook = buildExportWorkbook(results, t)
    XLSX.writeFile(workbook, `offline_audit_parity_${startDate}_${endDate}.xlsx`)
  }, [results, startDate, endDate, t])

  const hasAnyResult = Object.keys(results).length > 0

  return (
    <Stack spacing={3} className="audit-section">
      <h3>{t('audit_parity_title')}</h3>
      <Typography className="audit-section-note">{t('audit_parity_ground_truth_note')}</Typography>

      {!isOnline && <Alert severity="warning">{t('audit_parity_requires_online')}</Alert>}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          label={t('audit_parity_start_date')}
          type="date"
          size="small"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label={t('audit_parity_end_date')}
          type="date"
          size="small"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" onClick={runAll} disabled={!isOnline || runningEntity !== null}>
          {t('audit_parity_run_all')}
        </Button>
        <Button variant="outlined" onClick={handleExport} disabled={!hasAnyResult}>
          {t('audit_parity_export')}
        </Button>
      </Stack>

      {ENTITIES.map(entity => (
        <Accordion key={entity}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" spacing={2} alignItems="center" width="100%" justifyContent="space-between" pr={2}>
              <Typography>{t(`audit_entity_${entity}`)}</Typography>
              <Button
                size="small"
                variant="outlined"
                disabled={!isOnline || runningEntity !== null}
                onClick={(e) => { e.stopPropagation(); runOne(entity) }}
              >
                {runningEntity === entity ? t('audit_running') : t('audit_run_check')}
              </Button>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {errors[entity] && <Alert severity="error">{errors[entity]}</Alert>}
            {!errors[entity] && !results[entity] && (
              <Typography className="audit-section-note">{t('audit_parity_not_run_yet')}</Typography>
            )}
            {results[entity] && entity === 'visits' && (
              <Stack spacing={2}>
                <DiffSetSummary result={results[entity]} t={t} />
                <DiffSetDetails result={results[entity]} t={t} />
              </Stack>
            )}
            {results[entity] && entity === 'invitations' && (
              <Stack spacing={2}>
                <DiffSetSummary result={results[entity]} t={t} />
                <DiffSetDetails result={results[entity]} t={t} />
              </Stack>
            )}
            {results[entity] && entity === 'invitations_today' && (
              <Stack spacing={2}>
                <Typography className="audit-section-note">{t('audit_parity_invitations_today_note')}</Typography>
                <DiffSetSummary result={results[entity]} t={t} strict />
                <DiffSetDetails result={results[entity]} t={t} />
              </Stack>
            )}
            {results[entity] && entity === 'comes_from' && (
              <SubsetSummary result={results[entity]} t={t} boundedNote={t('audit_parity_comes_from_bounded_note')} />
            )}
            {results[entity] && entity === 'employees' && <SubsetSummary result={results[entity]} t={t} />}
            {results[entity] && entity === 'black_list' && <BlackListSummary result={results[entity]} t={t} />}
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  )
}

const mapStateToProps = state => ({
  isOnline: state.utilities.isOnline,
  enabledVisitHost: state.profile.enabled_visit_host,
})

export default connect(mapStateToProps)(ParityCheckPanel)
